// pastCanvasThumbnail — 過去キャンバスの最終 snapshot をサムネ用に合成 + 縮小する
// (past-canvas-new-document-2026-07-05.md §7 Slice 2 の「オンデマンド client 合成」)。
//
// フロー:
//   1. `snapshot-urls` から得た metaUrl + layerUrls を SnapshotLoader (`loadSnapshot`)
//      に渡し、背景色 + 各レイヤーの RGBA をデコードする (絵ログ / welcome と同一経路)。
//   2. 背景 → レイヤー (id 昇順・opacity 反映) の順に OffscreenCanvas へ合成し、
//      長辺 `maxEdge` に収まるよう縮小して Blob を返す。
//
// 署名 URL 失効 (403) は `loadSnapshot` が meta-not-found(403) / layer 全滅として
// 返すので、それを `expired` outcome にまとめる (= 呼び出し側が snapshot-urls を
// 再取得して 1 回だけリトライする)。それ以外の失敗は `error`。
//
// 2D canvas 前提 (welcome の SnapshotLoader デコードと同じ環境要件)。node vitest では
// 走らないが、本モジュールはブラウザ実行のサムネ合成専用で、単体テストはクライアント
// (fetch) 層に閉じる (task のテスト方針)。
//
// 2026-08-13 (Sentry WEB-5 と同根): 以前は **OffscreenCanvas 前提**と書いてあり実装も
// そうなっていたため、iOS 16.3 以前 (= OffscreenCanvas の 2D 未搭載) ではサムネが必ず
// 出なかった。合成先/作業用ともに `rasterCanvas` 経由にして `<canvas>` へ落とせるようにした。

import { createRaster2d, raster2dToBlob } from '../canvas2d/rasterCanvas.js';
import {
  type DecodedSnapshotLayer,
  loadSnapshot,
  type SnapshotLoaderDeps,
  type SnapshotMeta,
} from '../snapshot/SnapshotLoader.js';
import type { PastCanvasSnapshotUrls } from './pastCanvasClient.js';

export type ComposePastCanvasThumbnailOutcome =
  | { readonly outcome: 'ok'; readonly blob: Blob; readonly width: number; readonly height: number }
  /** 署名 URL が失効していた → 呼び出し側が snapshot-urls を再取得してリトライ。 */
  | { readonly outcome: 'expired' }
  | { readonly outcome: 'error' };

export interface ComposePastCanvasThumbnailOptions {
  /** サムネの長辺 (px)。ソースがこれ以下なら等倍 (never upscale)。 */
  readonly maxEdge: number;
  readonly signal?: AbortSignal;
}

export interface ComposePastCanvasThumbnailDeps {
  /** `loadSnapshot` を注入 (テスト用)。既定は本物。 */
  readonly loadSnapshotFn?: typeof loadSnapshot;
  /** SnapshotLoader へ渡す fetch/decode スタブ (テスト用)。 */
  readonly loaderDeps?: SnapshotLoaderDeps;
}

/**
 * `loadSnapshot` の error outcome が「署名 URL 失効」かを判定する。
 *   - meta 段の 403 (meta-not-found status 403)
 *   - layer 段の 403 (全レイヤーが `... returned 403` で落ちて invalid/layer になる)
 */
const isExpiryError = (outcome: {
  readonly cause: string;
  readonly status?: number;
  readonly stage?: string;
  readonly detail?: string;
}): boolean => {
  if (outcome.cause === 'meta-not-found' && outcome.status === 403) return true;
  if (outcome.cause === 'invalid' && outcome.stage === 'layer' && outcome.detail?.includes('403')) {
    return true;
  }
  return false;
};

const compositeToBlob = async (
  meta: SnapshotMeta,
  layers: readonly DecodedSnapshotLayer[],
  maxEdge: number,
): Promise<Blob | null> => {
  if (layers.length === 0) return null;
  const first = layers[0];
  if (first === undefined) return null;
  const { width: w, height: h } = first;
  if (w <= 0 || h <= 0) return null;

  const scale = Math.min(1, maxEdge / Math.max(w, h));
  const dw = Math.max(1, Math.round(w * scale));
  const dh = Math.max(1, Math.round(h * scale));

  // 2026-08-13 (Sentry WEB-5 と同根): 旧実装は `new OffscreenCanvas` 直呼びだったので
  // **iOS 16.3 以前ではサムネが必ず出なかった** (OffscreenCanvas の 2D は Safari 16.4
  // から)。ここはメインスレッド (`PastCanvasListModal`) なので `<canvas>` で代替できる。
  const dest = createRaster2d(dw, dh);
  if (dest === null) return null;
  const dctx = dest.ctx;

  // 背景色 (rgb 24bit + alpha 0..255) を敷く。alpha < 255 は棋盤ではなく素の
  // 半透明でよい (サムネなので透過部は panel 地が透ける ≒ 実表示に近い)。
  const bg = meta.background;
  const r = (bg.rgb >> 16) & 0xff;
  const g = (bg.rgb >> 8) & 0xff;
  const b = bg.rgb & 0xff;
  if (bg.alpha > 0) {
    dctx.fillStyle = `rgba(${r},${g},${b},${bg.alpha / 255})`;
    dctx.fillRect(0, 0, dw, dh);
  }

  // opacity を id 引きするための map (meta.layers の opacity を反映)。
  const opacityById = new Map<number, number>(meta.layers.map((l) => [l.id, l.opacity] as const));

  // 各レイヤーを原寸 temp に putImageData → dest へ縮小 drawImage (alpha-over 合成)。
  // putImageData は上書きなので compositing を効かせるため一旦 temp に置く。
  const temp = createRaster2d(w, h);
  if (temp === null) return null;
  const tctx = temp.ctx;
  const ordered = [...layers].sort((a, b2) => a.id - b2.id);
  for (const layer of ordered) {
    // 新規 Uint8ClampedArray にコピー = backing buffer を ArrayBuffer に確定させ、
    // ImageData コンストラクタの型 (SharedArrayBuffer 非許容) を満たす。
    const pixels = new Uint8ClampedArray(layer.rgba);
    tctx.putImageData(new ImageData(pixels, layer.width, layer.height), 0, 0);
    dctx.globalAlpha = opacityById.get(layer.id) ?? 1;
    dctx.drawImage(temp.canvas, 0, 0, w, h, 0, 0, dw, dh);
  }
  dctx.globalAlpha = 1;

  // OffscreenCanvas なら convertToBlob / `<canvas>` なら toBlob の差はヘルパが吸収。
  return await raster2dToBlob(dest);
};

/**
 * 過去キャンバスのサムネ Blob を合成する。失効時は `expired` を返し、呼び出し側が
 * `snapshot-urls` を再取得して 1 回だけ再試行する想定。
 */
export const composePastCanvasThumbnail = async (
  urls: PastCanvasSnapshotUrls,
  opts: ComposePastCanvasThumbnailOptions,
  deps: ComposePastCanvasThumbnailDeps = {},
): Promise<ComposePastCanvasThumbnailOutcome> => {
  const load = deps.loadSnapshotFn ?? loadSnapshot;
  const result = await load(
    {
      metaUrl: urls.metaUrl,
      layerUrls: urls.layerUrls,
      ...(opts.signal !== undefined ? { signal: opts.signal } : {}),
    },
    deps.loaderDeps ?? {},
  );
  if (result.outcome !== 'ok') {
    if (result.cause === 'aborted') return { outcome: 'error' };
    return isExpiryError(result) ? { outcome: 'expired' } : { outcome: 'error' };
  }
  try {
    const blob = await compositeToBlob(result.meta, result.layers, opts.maxEdge);
    if (blob === null) return { outcome: 'error' };
    // 合成した実寸を返す (component 側は object-fit で収める)。
    const first = result.layers[0];
    const w = first?.width ?? 0;
    const h = first?.height ?? 0;
    return { outcome: 'ok', blob, width: w, height: h };
  } catch {
    return { outcome: 'error' };
  }
};
